import { permissionMapHas, type PermissionMap } from '@/lib/permissions';
import type { ClinicRoleRow } from '@/hooks/useClinicRoles';

export type FormsStaffRole = 'admin' | 'editor';
export type FormsStaffStoredRole = 'admin' | 'editor' | 'owner';

export type FormsStaffMembership = {
  role: 'main_clinic_admin' | 'clinic_admin' | 'employee';
  custom_role_id: string | null;
  permissions?: unknown;
};

export function defaultFormsStaffRole(
  membershipRole: FormsStaffMembership['role'],
): FormsStaffRole {
  return membershipRole === 'clinic_admin' || membershipRole === 'main_clinic_admin'
    ? 'admin'
    : 'editor';
}

export const ACCOUNT_IN_OTHER_TENANT = 'account-in-other-tenant';

export function isActiveFormsGrant(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'synced' || status === 'error';
}

export function isAccountInOtherTenantSyncError(value: string | null | undefined): boolean {
  return value === ACCOUNT_IN_OTHER_TENANT;
}

/** Grant that may open Forms via SSO. Never retry account-in-other-tenant. */
export function isUsableFormsGrant(row: {
  sync_status: string;
  sync_error?: string | null;
}): boolean {
  if (isAccountInOtherTenantSyncError(row.sync_error)) return false;
  return isActiveFormsGrant(row.sync_status);
}

export function membershipHasQuestionnairesEdit(
  member: FormsStaffMembership,
  roles: ClinicRoleRow[],
): boolean {
  if (member.role === 'clinic_admin' || member.role === 'main_clinic_admin') {
    return true;
  }
  const overrides = member.permissions;
  if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
    const rec = overrides as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(rec, 'questionnaires')) {
      return permissionMapHas(overrides as PermissionMap, 'questionnaires', 'update');
    }
  }
  const roleRow = member.custom_role_id
    ? roles.find((row) => row.id === member.custom_role_id)
    : roles.find((row) => row.system_key === 'employee');
  if (!roleRow) return false;
  return permissionMapHas(roleRow.permissions, 'questionnaires', 'update');
}

/**
 * Open a new tab synchronously, then set its location to the SSO URL.
 * Opening first avoids popup blockers on the async fetch.
 */
export async function openFormsSsoTab(
  fetchUrl: () => Promise<string>,
): Promise<void> {
  const popup = window.open('about:blank', '_blank');
  if (popup) popup.opener = null;
  try {
    const url = await fetchUrl();
    if (popup) {
      popup.location.href = url;
      return;
    }
    window.location.assign(url);
  } catch (err) {
    popup?.close();
    throw err;
  }
}
